import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Performer } from "@shared/schema";
import type { ParticipantMode } from "./mode-config";

interface UseParticipantMutationsProps {
  jamNightId?: number;
  mode: ParticipantMode;
  performers: Performer[];
  onStageModal?: {
    setSelectedPerformer: (performer: Performer | null) => void;
    setShowStageModal: (show: boolean) => void;
  };
}

export function useParticipantMutations({
  jamNightId,
  mode,
  performers,
  onStageModal,
}: UseParticipantMutationsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updatePerformerMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<Performer>;
    }) => {
      const response = await apiRequest("PATCH", `/api/performers/${id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      if (jamNightId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/performers?jamNightId=${jamNightId}`],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/performers"] });
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update performer",
        variant: "destructive",
      });
    },
  });

  const removePerformerMutation = useMutation({
    mutationFn: async (performerId: number) => {
      const response = await apiRequest("PATCH", `/api/performers/${performerId}`, { removed: true });
      return response.json();
    },
    onSuccess: () => {
      if (jamNightId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/performers?jamNightId=${jamNightId}`],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/performers"] });
      }
      toast({
        title: "Success",
        description: "Performer removed successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove performer",
        variant: "destructive",
      });
    },
  });

  const handleActionToggle = (performer: Performer) => {
    if (mode === 'generic-event') {
      // Generic Event: Mark attended
      updatePerformerMutation.mutate({
        id: performer.id,
        updates: { attended: true },
      });
    } else if (mode === 'open-mic') {
      // Open Mic: Simple on/off stage
      updatePerformerMutation.mutate({
        id: performer.id,
        updates: {
          status: 'on_stage',
          stageStartTime: new Date(),
        },
      });
    } else {
      // Jam Night: Complex stage toggle
      if (performer.status === 'on_stage') {
        const elapsed = performer.stageStartTime 
          ? Math.floor((Date.now() - new Date(performer.stageStartTime).getTime()) / 1000 / 60)
          : 0;

        updatePerformerMutation.mutate({
          id: performer.id,
          updates: {
            status: 'participant',
            currentInstruments: [],
            timeOnStage: (performer.timeOnStage || 0) + elapsed,
            played: true,
            replacementName: null,
            stageStartTime: null,
          },
        });
      } else {
        const onStagePerformers = performers.filter((p) => p.status === 'on_stage');
        const availableInstruments = performer.instruments || [];

        if (onStagePerformers.length === 0 && availableInstruments.length <= 1) {
          updatePerformerMutation.mutate({
            id: performer.id,
            updates: {
              status: 'selected',
              currentInstruments: availableInstruments,
              replacingPerformerId: null,
            },
          });
        } else if (onStageModal) {
          onStageModal.setSelectedPerformer(performer);
          onStageModal.setShowStageModal(true);
        }
      }
    }
  };

  const handleStageSelection = (
    selectedPerformer: Performer,
    selectedInstruments: string[],
    replacementPerformerId?: number,
  ) => {
    if (replacementPerformerId) {
      const replacedPerformer = performers.find((p) => p.id === replacementPerformerId);
      if (replacedPerformer) {
        updatePerformerMutation.mutate({
          id: replacementPerformerId,
          updates: {
            status: 'pending_removal',
            replacementName: selectedPerformer.name,
          },
        });
      }
    }

    updatePerformerMutation.mutate({
      id: selectedPerformer.id,
      updates: {
        status: 'selected',
        currentInstruments: selectedInstruments,
        replacingPerformerId: replacementPerformerId || null,
      },
    });
  };

  const handleRemovePerformer = (performerId: number) => {
    if (mode === 'generic-event') {
      updatePerformerMutation.mutate({
        id: performerId,
        updates: {
          removed: true,
          attended: false,
        },
      });
    } else {
      updatePerformerMutation.mutate({
        id: performerId,
        updates: {
          removed: true,
          status: 'participant',
          stageStartTime: null,
        },
      });
    }
  };

  const handleConsider = (performer: Performer) => {
    updatePerformerMutation.mutate({
      id: performer.id,
      updates: {
        status: 'consideration',
      },
    });
  };

  return {
    updatePerformerMutation,
    removePerformerMutation,
    handleActionToggle,
    handleStageSelection,
    handleRemovePerformer,
    handleConsider,
    isLoading: updatePerformerMutation.isPending || removePerformerMutation.isPending,
  };
}